import { useMemo } from 'react'
import { useViewportStore, useSettingsStore } from '@/stores'

// Canvas-space grid spacing (units)
const MINOR = 24
const MAJOR = MINOR * 5  // 120

/**
 * Renders a subtle line grid as a CSS background on an absolutely positioned overlay.
 * The grid is applied to the canvas *container* (not the board), so the offset
 * calculation compensates for the board's transform to keep grid lines aligned
 * with canvas-space multiples of MINOR/MAJOR.
 *
 * Offset formula (derived from legacy updateGrid() in js/render.js):
 *   offsetX = (sc * tx % sizePx + sizePx) % sizePx
 *
 * This ensures that a grid line at canvas x=0 appears at screen x = sc*tx
 * (wrapping as the board pans).
 */
export function Grid() {
  const sc   = useViewportStore((s) => s.sc)
  const tx   = useViewportStore((s) => s.tx)
  const ty   = useViewportStore((s) => s.ty)
  const showGrid      = useSettingsStore((s) => s.showGrid)
  const resolvedTheme = useSettingsStore((s) => s.resolvedTheme)

  const style = useMemo<React.CSSProperties>(() => {
    if (!showGrid) return { display: 'none' }

    const isDark   = resolvedTheme === 'dark'
    const minorPx  = MINOR * sc
    const majorPx  = MAJOR * sc

    const offXMinor = ((sc * tx % minorPx) + minorPx) % minorPx
    const offYMinor = ((sc * ty % minorPx) + minorPx) % minorPx
    const offXMajor = ((sc * tx % majorPx) + majorPx) % majorPx
    const offYMajor = ((sc * ty % majorPx) + majorPx) % majorPx

    const minorColor = isDark ? 'rgba(120,145,170,.035)' : 'rgba(120,140,165,.05)'
    const majorColor = isDark ? 'rgba(130,155,180,.07)'  : 'rgba(120,140,165,.09)'

    return {
      backgroundImage: [
        `linear-gradient(${minorColor} 1px, transparent 1px)`,
        `linear-gradient(90deg, ${minorColor} 1px, transparent 1px)`,
        `linear-gradient(${majorColor} 1px, transparent 1px)`,
        `linear-gradient(90deg, ${majorColor} 1px, transparent 1px)`,
      ].join(', '),
      backgroundSize: [
        `${minorPx}px ${minorPx}px`,
        `${minorPx}px ${minorPx}px`,
        `${majorPx}px ${majorPx}px`,
        `${majorPx}px ${majorPx}px`,
      ].join(', '),
      backgroundPosition: [
        `${offXMinor}px ${offYMinor}px`,
        `${offXMinor}px ${offYMinor}px`,
        `${offXMajor}px ${offYMajor}px`,
        `${offXMajor}px ${offYMajor}px`,
      ].join(', '),
    }
  }, [sc, tx, ty, showGrid, resolvedTheme])

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        ...style,
      }}
    />
  )
}
